import { useState } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Clock, Star, Search, Plus } from "lucide-react";
import { useFoodLogs } from "@/hooks/useFoodLogs";
import { useFoods } from "@/hooks/useFoods";
import { useFavorites } from "@/hooks/useFavorites";
import { useAuth } from "@/contexts/AuthContext";
import { CATEGORY_COLORS } from "@/constants/foodUI";
import { Badge } from "@/components/ui/badge";

interface QuickAddSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFoodSelect: (foodId: string, foodName: string) => void;
}

export function QuickAddSheet({ open, onOpenChange, onFoodSelect }: QuickAddSheetProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const { getRecentFoods } = useFoodLogs();
  const { foods } = useFoods();
  const { favorites } = useFavorites();
  const { selectedBaby } = useAuth();

  // Get recent foods (last 10 unique foods)
  const recentLogs = getRecentFoods();
  const recentFoodIds = Array.from(new Set(
    recentLogs
      .filter(log => !selectedBaby || log.baby_id === selectedBaby.id)
      .map(log => log.food_id)
  )).slice(0, 10);
  
  const recentFoods = recentFoodIds
    .map(id => foods.find(f => f.id === id))
    .filter((f): f is NonNullable<typeof f> => f !== undefined);

  // Get favorite foods
  const favoriteFoods = foods.filter(f => favorites.includes(f.id));

  // Search foods
  const searchResults = searchQuery.trim() === "" 
    ? [] 
    : foods.filter(f => 
        f.name.toLowerCase().includes(searchQuery.toLowerCase())
      ).slice(0, 20);

  const getCategoryColor = (category: string) => {
    return CATEGORY_COLORS[category as keyof typeof CATEGORY_COLORS] || CATEGORY_COLORS.other;
  };

  const FoodListItem = ({ food }: { food: any }) => (
    <Button
      variant="ghost"
      className="w-full justify-start h-auto p-3 hover:bg-muted"
      onClick={() => {
        onFoodSelect(food.id, food.name);
        onOpenChange(false);
      }}
    >
      <div 
        className="w-1 h-10 rounded-full mr-3"
        style={{ backgroundColor: getCategoryColor(food.category) }}
      />
      <div className="flex-1 text-left">
        <p className="font-medium">{food.name}</p>
        <div className="flex items-center gap-2 mt-1">
          {food.age_introduction && (
            <Badge variant="outline" className="text-xs">
              {food.age_introduction}+ mnd
            </Badge>
          )}
          {food.allergens && food.allergens.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              ⚠️ Allergen
            </Badge>
          )}
        </div>
      </div>
      <Plus className="h-4 w-4 text-muted-foreground" />
    </Button>
  );

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[85vh]">
        <DrawerHeader>
          <DrawerTitle>Legg til mat</DrawerTitle>
          <DrawerDescription>
            Velg en matvare fra nylig brukte, favoritter eller søk
          </DrawerDescription>
        </DrawerHeader>

        <Tabs defaultValue="recent" className="flex-1 flex flex-col px-4 pb-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="recent" className="gap-2">
              <Clock className="h-4 w-4" />
              <span className="hidden sm:inline">Nylig</span>
            </TabsTrigger>
            <TabsTrigger value="favorites" className="gap-2">
              <Star className="h-4 w-4" />
              <span className="hidden sm:inline">Favoritter</span>
            </TabsTrigger>
            <TabsTrigger value="search" className="gap-2">
              <Search className="h-4 w-4" />
              <span className="hidden sm:inline">Søk</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="recent" className="flex-1 mt-4">
            <ScrollArea className="h-[50vh]">
              {recentFoods.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">Ingen nylige matvarer</p>
                  <p className="text-xs mt-1">Start å logge mat for å se dem her!</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {recentFoods.map(food => (
                    <FoodListItem key={food.id} food={food} />
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="favorites" className="flex-1 mt-4">
            <ScrollArea className="h-[50vh]">
              {favoriteFoods.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">Ingen favoritter ennå</p>
                  <p className="text-xs mt-1">Merk matvarer som favoritter for rask tilgang!</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {favoriteFoods.map(food => (
                    <FoodListItem key={food.id} food={food} />
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="search" className="flex-1 mt-4 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Søk etter matvare..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                autoFocus
              />
            </div>
            
            <ScrollArea className="h-[45vh]">
              {searchQuery.trim() === "" ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Skriv for å søke etter matvarer</p>
                </div>
              ) : searchResults.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">Ingen resultater for "{searchQuery}"</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {searchResults.map(food => (
                    <FoodListItem key={food.id} food={food} />
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DrawerContent>
    </Drawer>
  );
}
